/* Brown Butter – Collections directory behaviour (search, mobile accordions, balanced columns) */
if (!customElements.get('bb-collections-directory')) {
  customElements.define(
    'bb-collections-directory',
    class BBCollectionsDirectory extends HTMLElement {
      connectedCallback() {
        this.mq = window.matchMedia('(min-width: 750px)');
        this.grid = this.querySelector('[data-bb-grid]');
        this.groups = Array.from(this.querySelectorAll('[data-bb-group]'));
        this.toggles = this.groups.map((g) => g.querySelector('[data-bb-toggle]'));
        this.items = Array.from(this.querySelectorAll('[data-bb-item]'));
        this.input = this.querySelector('[data-bb-search]');
        this.clearBtn = this.querySelector('[data-bb-clear]');
        this.icon = this.querySelector('[data-bb-search-icon]');
        this.empty = this.querySelector('[data-bb-empty]');
        this.emptyQuery = this.querySelector('[data-bb-empty-query]');
        this.status = this.querySelector('[data-bb-status]');
        this.featureWrap = this.querySelector('[data-bb-feature-wrap]');
        this.terms = [];

        this.items.forEach((el) => {
          el.dataset.bbKey = this.normalize(el.dataset.bbName || el.textContent);
        });

        this.toggles.forEach((t, i) => t && t.addEventListener('click', () => this.onToggle(i)));

        if (this.input) {
          this.input.addEventListener('input', () => this.filter(this.input.value));
          this.input.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.input.value) {
              e.preventDefault();
              this.reset();
            }
            if (e.key === 'Enter') e.preventDefault();
          });
        }
        if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.reset(true));
        const resetBtn = this.querySelector('[data-bb-reset]');
        if (resetBtn) resetBtn.addEventListener('click', () => this.reset(true));

        this.onMq = () => this.applyMode();
        if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMq);
        else this.mq.addListener(this.onMq);

        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => this.scheduleLayout());
          this.groups.forEach((g) => this.ro.observe(g));
        } else {
          window.addEventListener('resize', () => this.scheduleLayout());
        }

        this.onBlockSelect = (e) => {
          const i = this.groups.indexOf(e.target);
          if (i > -1 && !this.isDesktop()) this.openOnly(i);
        };
        document.addEventListener('shopify:block:select', this.onBlockSelect);

        // A pre-filled field (back/forward cache) should still filter.
        if (this.input && this.input.value) this.filter(this.input.value);
        else this.applyMode();
      }

      disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
        if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMq);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      isDesktop() {
        return this.mq.matches;
      }

      normalize(s) {
        return String(s || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/&/g, ' and ')
          .replace(/[^a-z0-9]+/g, ' ')
          .trim();
      }

      setOpen(i, open) {
        const g = this.groups[i];
        const t = this.toggles[i];
        g.classList.toggle('is-open', open);
        if (t) t.setAttribute('aria-expanded', String(open));
      }

      openOnly(i) {
        this.groups.forEach((_, j) => this.setOpen(j, j === i));
      }

      applyMode() {
        const desktop = this.isDesktop();
        this.toggles.forEach((t, i) => {
          if (!t) return;
          if (desktop) {
            t.setAttribute('tabindex', '-1');
            t.setAttribute('aria-disabled', 'true');
            this.setOpen(i, true);
          } else {
            t.removeAttribute('tabindex');
            t.removeAttribute('aria-disabled');
            // Searching: expand groups with matches. Otherwise: default collapsed.
            this.setOpen(i, this.terms.length > 0 && !this.groups[i].hidden);
          }
        });
        this.layout();
      }

      onToggle(i) {
        if (this.isDesktop()) return;
        const willOpen = !this.groups[i].classList.contains('is-open');
        if (this.terms.length) {
          this.setOpen(i, willOpen);
        } else {
          this.groups.forEach((_, j) => this.setOpen(j, j === i && willOpen));
        }
        if (willOpen) {
          const top = this.toggles[i].getBoundingClientRect().top;
          if (top < 0) this.toggles[i].scrollIntoView({ block: 'start' });
        }
      }

      filter(value) {
        this.terms = this.normalize(value).split(' ').filter(Boolean);
        const active = this.terms.length > 0;
        let count = 0;

        this.items.forEach((el) => {
          const show = !active || this.terms.every((t) => el.dataset.bbKey.includes(t));
          el.hidden = !show;
          if (show) count++;
        });
        this.groups.forEach((g) => {
          g.hidden = active && !g.querySelector('[data-bb-item]:not([hidden])');
        });
        if (this.featureWrap) {
          const link = this.featureWrap.querySelector('[data-bb-item]');
          this.featureWrap.hidden = !!link && link.hidden;
        }

        this.classList.toggle('is-searching', active);
        if (this.clearBtn) this.clearBtn.hidden = !value;
        if (this.icon) this.icon.hidden = !!value;
        if (this.empty) {
          this.empty.hidden = !active || count > 0;
          if (this.emptyQuery) this.emptyQuery.textContent = value.trim();
        }

        clearTimeout(this.statusTimer);
        this.statusTimer = setTimeout(() => {
          if (!this.status) return;
          this.status.textContent = active
            ? count === 0
              ? 'No collections found'
              : `${count} collection${count === 1 ? '' : 's'} found`
            : '';
        }, 350);

        this.applyMode();
      }

      reset(focus) {
        if (this.input) this.input.value = '';
        this.filter('');
        if (focus && this.input) this.input.focus();
      }

      scheduleLayout() {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.layout());
      }

      // Balanced two-column layout that keeps DOM (reading) order.
      layout() {
        if (!this.grid) return;
        if (!this.isDesktop()) {
          this.grid.classList.remove('is-masonry');
          this.groups.forEach((g) => (g.style.gridRowEnd = ''));
          return;
        }
        const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 24;
        this.grid.classList.add('is-masonry');
        this.groups.forEach((g) => {
          if (g.hidden) return;
          const h = g.getBoundingClientRect().height;
          const span = `span ${Math.ceil(h + gap)}`;
          if (g.style.gridRowEnd !== span) g.style.gridRowEnd = span;
        });
      }
    }
  );
}
